// Shapes returned by the HTTP API (/api/*).
import type { TimestampFormat } from "./timestamps.ts";
import type { LinkPreviewChoices } from "./linkPreviews.ts";
import type { Role } from "./protocol.ts";
import { DEFAULT_IDLE_PUSH_MINUTES } from "./idlePush.ts";

export interface Me {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  /** Avatar URL (/api/users/<id>/avatar/<file>), or null for the coloured initials. */
  image: string | null;
  /** The avatar is a copy of my Gravatar, checked once a day (Settings → My account → Avatar → Use Gravatar). */
  gravatar?: boolean;
  servers: ServerSummary[];
  /** Preferences that follow me to every device (also GET/PUT /api/me/settings). */
  settings: UserSettings;
}

/** Server colours (the rail tile): new servers get one at random; owners and admins can pick another. */
export const SERVER_COLORS = ["#C2255C", "#0E7490", "#7C3AED", "#B45309", "#2563EB", "#4D7C0F", "#B91C1C", "#0F766E", "#475569"];

/**
 * Themes (Settings → Appearance). Each is a full set of styles.css's theme variables (`:root[data-palette="<id>"]`,
 * `.theme-<id>`) on top of a base scheme, light or dark, which is what anything that only knows two themes reads
 * (<html data-theme>, app frames, card previews, native controls). `bar` is the theme's `--surface` (the chat
 * header), used for the phone's status bar; web/index.html repeats id → scheme and bar so it's right before first
 * paint (scripts/theme-check.mjs keeps the three in step). At most 8, not counting System.
 */
export type ThemeScheme = "light" | "dark";
export type ThemeId = "light" | "dark" | "midnight" | "dracula" | "nord" | "gruvbox" | "solarized" | "latte";
export interface ThemeInfo {
  id: ThemeId;
  label: string;
  scheme: ThemeScheme;
  bar: string;
  /** One line for the swatch's tooltip and the settings help. */
  blurb: string;
}
export const THEMES: ThemeInfo[] = [
  { id: "light", label: "Light", scheme: "light", bar: "#ffffff", blurb: "Bloom's own: white chat, warm grey sidebars, magenta." },
  { id: "dark", label: "Dark", scheme: "dark", bar: "#1e2025", blurb: "A calm charcoal with lifted magenta." },
  { id: "midnight", label: "Midnight", scheme: "dark", bar: "#000000", blurb: "True black for OLED screens, like Discord's Midnight." },
  { id: "dracula", label: "Dracula", scheme: "dark", bar: "#282a36", blurb: "Purple-grey night with neon purple, pink and cyan." },
  { id: "nord", label: "Nord", scheme: "dark", bar: "#2e3440", blurb: "Arctic slate blues with frosty cyan." },
  { id: "gruvbox", label: "Gruvbox", scheme: "dark", bar: "#282828", blurb: "Retro warm browns with orange and cream." },
  { id: "solarized", label: "Solarized Light", scheme: "light", bar: "#fdf6e3", blurb: "Soft cream paper with teal-grey ink." },
  { id: "latte", label: "Catppuccin Latte", scheme: "light", bar: "#eff1f5", blurb: "Pastel cool grey with mauve." },
];
export const THEME_IDS: ThemeId[] = THEMES.map((t) => t.id);
/** "System" follows the OS between Light and Dark. */
export type ThemePref = "system" | ThemeId;
export const THEME_PREFS: ThemePref[] = ["system", ...THEME_IDS];
/** What clients that only know light and dark understand (UserSettings.theme). */
export type BaseThemePref = "system" | ThemeScheme;
export const BASE_THEME_PREFS: BaseThemePref[] = ["system", "light", "dark"];
export const themeInfo = (id: ThemeId): ThemeInfo => THEMES.find((t) => t.id === id) ?? THEMES[0]!;
/** The light/dark/system a choice reads as for clients that only know those. */
export const baseThemePref = (p: ThemePref): BaseThemePref => (p === "system" ? "system" : themeInfo(p).scheme);

/** GET/PUT /api/me/settings. PUT takes any subset and answers with the whole thing. */
/** How messages are laid out: avatars and the name above the text, Discord's compact rows (no avatars), or chat bubbles. */
export type MessageDisplay = "cozy" | "compact" | "bubbles";
export const MESSAGE_DISPLAYS: MessageDisplay[] = ["cozy", "compact", "bubbles"];

/** How big chat text is drawn (messages, names, the message box). */
export type TextSize = "small" | "medium" | "large";
export const TEXT_SIZES: TextSize[] = ["small", "medium", "large"];

export interface UserSettings {
  /** Light, dark or system: the base of the chosen theme, for clients that only know those (the iOS app). */
  theme: BaseThemePref;
  /**
   * The full choice (any theme). A PUT with only `theme` (an older client) keeps a named theme while its base is
   * unchanged and replaces it otherwise; a PUT with `themeName` sets both.
   */
  themeName: ThemePref;
  messageDisplay: MessageDisplay;
  textSize: TextSize;
  /** Show the activity meter beside each channel in the channel list. */
  channelActivity: boolean;
  /** How messages show their time (#52, shared/src/timestamps.ts). */
  timestampFormat: TimestampFormat;
  /** Play super reactions' effects (#87); off shows them as plain reactions. */
  reactionEffects: boolean;
  /** Strip known tracking IDs from the links in my messages before they are sent (#311, shared/src/trackingLinks.ts). */
  stripTracking: boolean;
  /**
   * Let the AIs of people I DM with read our DMs, when they allowed their AI to (docs/connector.md "Messages"). Off:
   * no one else's AI reads a DM I'm in. My own AI is my own choice, in Connected apps & AI.
   */
  dmAi: boolean;
  /**
   * Emoji starred in the picker, in the order they were starred (shared/src/favoriteEmoji.ts): Unicode emoji and
   * server emoji tokens, at most MAX_FAVORITE_EMOJI. A PUT with it replaces the whole list; clients send one change
   * at a time instead (UserSettingsUpdate).
   */
  favoriteEmoji: string[];
  /**
   * Which mirror makes the card for my links, per site (#578, shared/src/linkPreviews.ts). A missing site is
   * Automatic. A PUT with it changes only the sites it names: "auto" clears one, "unmodified" or a host on the
   * server's list (GET /api/link-previews) sets it.
   */
  linkPreviews: LinkPreviewChoices;
  /**
   * The color of my name (#631, #698): a #rrggbb of the name color palette (shared/src/nameColor.ts), the same in every theme. The
   * server stores one for every person, never null: the hash color of my id until I pick another. It is public: every member of every
   * server I am in sees it (`Member.nameColor`, `MemberProfile.nameColor`). Only I change it. A PUT with a palette hex (any case; stored
   * in lower case) sets it; a PUT with null stores the hash color again (Automatic); anything else is a 400.
   */
  nameColor: string;
  /**
   * When I am in use on a desktop, my phone gets a push after this many minutes if the message is still unread (#683,
   * shared/src/idlePush.ts): 0 right away, 1, 2, 5, 10, 15, 30, or -1 never (the phone gets nothing while a desktop is
   * active). Default 5. A PUT without it keeps the stored value; any other number is a 400.
   */
  idlePushMinutes: number;
}

/**
 * A PUT to /api/me/settings: any settings to change, or one change to the favorite emoji. Clients star and unstar
 * with `favoriteEmojiAdd` / `favoriteEmojiRemove`, which the server applies to the stored list (applyFavoriteChange),
 * so a device with an old copy can't undo another's; `favoriteEmoji` replaces the whole list.
 */
export type UserSettingsUpdate = Partial<Omit<UserSettings, "nameColor">> & { nameColor?: string | null; favoriteEmojiAdd?: string; favoriteEmojiRemove?: string };

export const DEFAULT_USER_SETTINGS: UserSettings = {
  theme: "system",
  themeName: "system",
  messageDisplay: "cozy",
  textSize: "medium",
  channelActivity: true,
  timestampFormat: "default",
  reactionEffects: true,
  stripTracking: true,
  dmAi: true,
  favoriteEmoji: [],
  linkPreviews: {},
  // A placeholder: the server always answers with the stored color (`ensureNameColor` in worker/src/nameColor.ts).
  nameColor: "#8a8a8a",
  idlePushMinutes: DEFAULT_IDLE_PUSH_MINUTES,
};

/** Avatars are cropped and resized in the browser; the Worker refuses anything bigger than this. */
export const MAX_AVATAR_BYTES = 512 * 1024;
export const MAX_NAME_LENGTH = 40;

/**
 * GET /api/me/unread: per server I'm in, whether a conversation I haven't muted has unread messages, and my unread
 * mentions (DMs count). The server menu's switcher and the dot by the server name (#140) show it for other servers.
 */
export type ServerUnread = Record<string, { unread: boolean; mentions: number }>;

/**
 * The layouts (#141, #154). "chats": one list of every chat across servers. "classic": one
 * server's channel list by category, with the unread pills and banner. "irc": a retro
 * layout after mIRC (#154): a black monospace pane with one line per message (`[18:20] <nick> text`), a switchbar of
 * windows, and a nick list grouped @ / + / the rest. Each layout lives in its own components, chosen in one place per
 * client, so any can be removed by deleting its files and the switch. Each device can override the default
 * (Settings → Appearance → Layout).
 */
export type ChatLayout = "chats" | "classic" | "irc";
export const CHAT_LAYOUTS: readonly ChatLayout[] = ["chats", "classic", "irc"];
export const DEFAULT_CHAT_LAYOUT: ChatLayout = "classic";

/**
 * One conversation in the list of chats (#141): a text channel, or a DM I'm in, from any of my servers. Clients sort
 * by `lastTs`, newest first, so every server's chats share one list.
 */
export interface ChatSummary {
  serverId: string;
  channelId: string;
  kind: "text" | "dm";
  /** Text channels: the name, without "#". DMs: "" (clients show the other members' names, from `people`). */
  name: string;
  /** DMs only: the user ids in it, including mine, sorted. */
  members?: string[];
  lastSeq: number;
  /** When the newest message was posted (ms), or null when nothing has been posted yet. */
  lastTs: number | null;
  /** Whoever posted the newest message, or null. */
  lastAuthorId: string | null;
  /** The newest message as plain text, without its author ("see you at 8"; the same words as its push), at most 180 characters; "" when empty. */
  preview: string;
  /** Something after my read position, and the conversation isn't muted. */
  unread: boolean;
  /** My unread @mentions in it. */
  mentions: number;
  /** I muted it (or its server), so it never counts as unread. */
  muted: boolean;
  /** A DM I closed with × and nobody has written in since: hide it from the list. */
  closed: boolean;
}

/** GET /api/me/chats: every conversation in every server I'm in, and the names and pictures of the people in my DMs. */
export interface MyChats {
  chats: ChatSummary[];
  /** The people in my DMs (and who wrote each newest message), by user id. */
  people: Record<string, { name: string; image: string | null }>;
}

export interface ServerSummary {
  id: string;
  name: string;
  color: string;
  role: Role;
  membersCanInvite: boolean;
  /** Off: invites stop working until it's turned back on (server settings). */
  acceptingMembers: boolean;
  /** The server's picture in the server switcher (square), or null for its initials on its colour. */
  icon: string | null;
  /** A wide picture over the channel list, or null. */
  banner: string | null;
}

/** Server pictures (PUT /api/servers/:id/icon | banner), already cropped and resized in the browser. */
export type ServerImageKind = "icon" | "banner";
export const SERVER_IMAGE = {
  icon: { width: 256, height: 256, maxBytes: 512 * 1024 },
  banner: { width: 960, height: 540, maxBytes: 1024 * 1024 },
} as const;

/** GET /api/config: settings the web app needs. */
export interface ClientConfig {
  /** Null: GIFs aren't set up yet. */
  klipyKey: string | null;
  /** DEV_MODE with `KLIPY_FAKE=1` only (#588): the base address of the fake KLIPY, in place of https://api.klipy.com. */
  klipyBase?: string | null;
  maxFileBytes: number;
  /**
   * Where app full views are served from: a separate registrable domain (https://bloomapps.dev), so an app's frame
   * shares nothing with Bloom (no cookies, no same-site). Null: not set up, and full views say so.
   */
  appFrameOrigin: string | null;
  /** Send usage events to PostHog (web/src/analytics.ts). False on local and test servers (DEV_MODE). */
  analytics: boolean;
}

export interface Member {
  id: string;
  name: string;
  /** Avatar URL, or null for the coloured initials. */
  image: string | null;
  role: Role;
  joinedAt: number;
  /** The #rrggbb of this person's name color (#631, #698): stored on the server, never null, the same for every viewer. */
  nameColor: string;
}

export interface InvitePreview {
  code: string;
  valid: boolean;
  reason?: "expired" | "used_up" | "revoked" | "not_found" | "closed" | "banned";
  server?: { id: string; name: string; color: string; memberCount: number };
  invitedBy?: string;
}

export interface Invite {
  code: string;
  url: string;
  createdBy: string;
  createdAt: number;
  expiresAt: number | null;
  maxUses: number | null;
  uses: number;
  email: string | null;
}

/** POST /api/admin/servers/:id/import: categories and channels in order. Existing ones (by name) are moved, not duplicated. */
export interface StructureImport {
  categories: { name: string; channels: (string | { name: string; topic?: string })[] }[];
}

export interface ImportResult {
  categoriesCreated: number;
  channelsCreated: number;
  channelsMoved: number;
}

/**
 * cold_start: page load to the first channel full of messages on screen (from the device cache when there is one).
 * cold_start_live: page load to caught up with the room (every channel's missed messages in).
 */
export type LatencyKind = "send_ack" | "echo" | "typing" | "channel_switch" | "cold_start" | "cold_start_live" | "reconnect" | "ping";

export interface LatencySample {
  kind: LatencyKind;
  ms: number;
}

export interface LatencySummary {
  windowHours: number;
  rows: { kind: LatencyKind; count: number; p50: number; p95: number; max: number }[];
}

/**
 * GET /api/servers/:id/agent (owners and admins): @Bloom's spending this calendar month (UTC) against the server's
 * cap. PUT { capUsd } changes the cap (owner only). Members never see costs.
 */
export interface AgentSummary {
  /** "2026-10" */
  month: string;
  spentUsd: number;
  capUsd: number;
  /** Replies @Bloom gave this month (refusals not counted). */
  replies: number;
  /** Who asked, most spent first (top 5). */
  topSpenders: { userId: string; usd: number; replies: number }[];
  /** Spend on replies that used an app's tools, by app (a reply using two apps counts for both). */
  byApp: { app: string; usd: number; replies: number }[];
  /** The model @Bloom answers with. */
  model: string;
  /** False until federation (or a dev API key) is configured: @Bloom then says it isn't set up yet. */
  configured: boolean;
  /** The caller may change the cap (the owner). */
  canEdit: boolean;
  /** Who may bloom apps here (the owner chooses). */
  bloomWho: "everyone" | "admins";
}

/** Highest monthly cap an owner can set, as a guard against typos (owners can ask for more). */
export const MAX_AGENT_CAP_USD = 500;

/** GET /api/servers/:id/bans (owners and admins). */
export interface Ban {
  userId: string;
  name: string;
  bannedBy: string;
  reason: string | null;
  createdAt: number;
}
